"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChangesetOperations = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const base_1 = require("../../base");
class ChangesetOperations extends base_1.OperationsBase {
    constructor(options, _namedVersionOperations, _checkpointOperations) {
        super(options);
        this._namedVersionOperations = _namedVersionOperations;
        this._checkpointOperations = _checkpointOperations;
    }
    /**
     * Gets Changesets for a specific iModel. This method returns Changesets in their minimal representation. The
     * returned iterator internally queries entities in pages. Wraps the
     * {@link https://developer.bentley.com/apis/imodels/operations/get-imodel-changesets/ Get iModel Changesets}
     * operation from iModels API.
     * @param {GetChangesetListParams} params parameters for this operation. See {@link GetChangesetListParams}.
     * @returns {EntityListIterator<MinimalChangeset>} iterator for Changeset list. See {@link EntityListIterator},
     * {@link MinimalChangeset}.
     */
    getMinimalList(params) {
        return new base_1.EntityListIteratorImpl(async () => this.getEntityCollectionPage({
            authorization: params.authorization,
            url: this._options.urlFormatter.getChangesetListUrl({ iModelId: params.iModelId, urlParams: params.urlParams }),
            preferReturn: base_1.PreferReturn.Minimal,
            entityCollectionAccessor: (response) => response.changesets
        }));
    }
    /**
     * Gets Changesets for a specific iModel. This method returns Changesets in their full representation. The returned
     * iterator internally queries entities in pages. Wraps the
     * {@link https://developer.bentley.com/apis/imodels/operations/get-imodel-changesets/ Get iModel Changesets}
     * operation from iModels API.
     * @param {GetChangesetListParams} params parameters for this operation. See {@link GetChangesetListParams}.
     * @returns {EntityListIterator<Changeset>} iterator for Changeset list. See {@link EntityListIterator},
     * {@link Changeset}.
     */
    getRepresentationList(params) {
        const entityCollectionAccessor = (response) => {
            const changesets = response.changesets;
            const mappedChangesets = changesets.map((changeset) => this.appendRelatedEntityCallbacks(params.authorization, changeset));
            return mappedChangesets;
        };
        return new base_1.EntityListIteratorImpl(async () => this.getEntityCollectionPage({
            authorization: params.authorization,
            url: this._options.urlFormatter.getChangesetListUrl({ iModelId: params.iModelId, urlParams: params.urlParams }),
            preferReturn: base_1.PreferReturn.Representation,
            entityCollectionAccessor
        }));
    }
    /**
     * Gets a single Changeset identified by either index or id. This method returns a Changeset in its full
     * representation. Wraps the {@link https://developer.bentley.com/apis/imodels/operations/get-imodel-changeset-details/
     * Get iModel Changeset} operation from iModels API.
     * @param {GetSingleChangesetParams} params parameters for this operation. See {@link GetSingleChangesetParams}.
     * @returns {Promise<Changeset>} a Changeset with specified id or index. See {@link Changeset}.
     */
    async getSingle(params) {
        const changeset = await this.querySingleInternal(params);
        const result = this.appendRelatedEntityCallbacks(params.authorization, changeset);
        return result;
    }
    async querySingleInternal(params) {
        const { authorization, iModelId, ...changesetIdOrIndex } = params;
        const response = await this.sendGetRequest({
            authorization,
            url: this._options.urlFormatter.getSingleChangesetUrl({ iModelId, ...changesetIdOrIndex })
        });
        return response.changeset;
    }
    appendRelatedEntityCallbacks(authorization, changeset) {
        const getNamedVersion = async () => { var _a; return this.getNamedVersion(authorization, (_a = changeset._links.namedVersion) === null || _a === void 0 ? void 0 : _a.href); };
        const getCurrentOrPrecedingCheckpoint = async () => { var _a; return this.getCurrentOrPrecedingCheckpoint(authorization, (_a = changeset._links.currentOrPrecedingCheckpoint) === null || _a === void 0 ? void 0 : _a.href); };
        const result = {
            ...changeset,
            getNamedVersion,
            getCurrentOrPrecedingCheckpoint
        };
        return result;
    }
    async getNamedVersion(authorization, namedVersionLink) {
        if (!namedVersionLink)
            return undefined;
        const { iModelId, namedVersionId } = this._options.urlFormatter.parseNamedVersionUrl(namedVersionLink);
        return this._namedVersionOperations.getSingle({
            authorization,
            iModelId,
            namedVersionId
        });
    }
    async getCurrentOrPrecedingCheckpoint(authorization, currentOrPrecedingCheckpointLink) {
        if (!currentOrPrecedingCheckpointLink)
            return undefined;
        const { iModelId, changesetIndex } = this._options.urlFormatter.parseCheckpointUrl(currentOrPrecedingCheckpointLink);
        return this._checkpointOperations.getSingle({
            authorization,
            iModelId,
            changesetIndex
        });
    }
}
exports.ChangesetOperations = ChangesetOperations;
//# sourceMappingURL=ChangesetOperations.js.map